import React, { useState, useCallback, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { LVProduct } from '@/features/product-library/types';

interface ProductImageCalloutProps {
  product: LVProduct;
  imageUrl: string;
  onPositionChange: (x: number, y: number) => void;
  onSizeChange: (width: number, height: number) => void;
  onClose: () => void;
  scale: number;
  isSelected?: boolean;
  isPublicView?: boolean;
  /** Live icon position - updates during icon drag */
  iconPosition?: { x: number; y: number };
  /** Size of the icon marker (default: 28px - the icon container size) */
  iconSize?: number;
}

/**
 * ProductImageCallout - Shows a product image on the floor plan connected to its icon
 * with a line. Supports drag-to-reposition and resize handles.
 */
export function ProductImageCallout({
  product,
  imageUrl,
  onPositionChange,
  onSizeChange,
  onClose,
  scale,
  isSelected = false,
  isPublicView = false,
  iconPosition,
  iconSize = 28,
}: ProductImageCalloutProps) {
  // Get current image position or calculate default offset from product
  const defaultOffsetX = 100;
  const defaultOffsetY = -100;

  const imageX = product.product_image_x ?? product.x_position + defaultOffsetX;
  const imageY = product.product_image_y ?? product.y_position + defaultOffsetY;
  const imageWidth = product.product_image_width ?? 120;
  const imageHeight = product.product_image_height ?? 120;

  // Local state for dragging
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [localPos, setLocalPos] = useState({ x: imageX, y: imageY });
  const [localSize, setLocalSize] = useState({ width: imageWidth, height: imageHeight });

  // Use refs to track current values for event handlers (avoids stale closures)
  const localPosRef = useRef(localPos);
  const localSizeRef = useRef(localSize);

  // Track if we just finished dragging/resizing to prevent snap-back
  const justFinishedDraggingRef = useRef(false);
  const justFinishedResizingRef = useRef(false);

  // Track last icon position to prevent snap-back when icon drag ends
  // This caches the position when iconPosition becomes undefined (drag released)
  const lastIconPositionRef = useRef<{ x: number; y: number } | null>(null);
  const justFinishedIconDragRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => {
    localPosRef.current = localPos;
  }, [localPos]);

  useEffect(() => {
    localSizeRef.current = localSize;
  }, [localSize]);

  const dragStartRef = useRef<{ x: number; y: number; posX: number; posY: number } | null>(null);
  const resizeStartRef = useRef<{
    x: number;
    y: number;
    width: number;
    height: number;
    corner: string;
  } | null>(null);

  // Sync local state with props when they change externally
  // Skip sync if we just finished dragging (props are stale, local state is correct)
  useEffect(() => {
    if (!isDragging && !justFinishedDraggingRef.current) {
      setLocalPos({ x: imageX, y: imageY });
    }
    // Clear the flag after props update
    if (justFinishedDraggingRef.current) {
      justFinishedDraggingRef.current = false;
    }
  }, [imageX, imageY, isDragging]);

  useEffect(() => {
    if (!isResizing && !justFinishedResizingRef.current) {
      setLocalSize({ width: imageWidth, height: imageHeight });
    }
    // Clear the flag after props update
    if (justFinishedResizingRef.current) {
      justFinishedResizingRef.current = false;
    }
  }, [imageWidth, imageHeight, isResizing]);

  // Track icon position changes to prevent snap-back when icon drag ends
  // When iconPosition goes from defined to undefined, cache the last value
  useEffect(() => {
    if (iconPosition) {
      // Icon is being dragged - update the cached position
      lastIconPositionRef.current = { x: iconPosition.x, y: iconPosition.y };
      justFinishedIconDragRef.current = false; // Reset flag while dragging
    } else if (lastIconPositionRef.current) {
      // Icon drag just ended (iconPosition became undefined)
      // Keep using cached position until product props update
      justFinishedIconDragRef.current = true;
    }
  }, [iconPosition]);

  // Clear the icon drag flag when product position props update to match cached position
  // Use a small delay to ensure at least one render uses the cached position
  useEffect(() => {
    if (justFinishedIconDragRef.current && lastIconPositionRef.current) {
      // Check if product position has been updated to match (or be close to) cached position
      const cachedPos = lastIconPositionRef.current;
      const productPos = { x: product.x_position, y: product.y_position };
      const distance = Math.sqrt(
        Math.pow(cachedPos.x - productPos.x, 2) + Math.pow(cachedPos.y - productPos.y, 2),
      );

      // If positions are close enough (DB update has propagated), clear the cache
      if (distance < 5) {
        justFinishedIconDragRef.current = false;
        lastIconPositionRef.current = null;
      }
    }
  }, [product.x_position, product.y_position]);

  // Handle drag start
  const handleDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (isPublicView) return;

      e.stopPropagation();
      e.preventDefault();

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

      setIsDragging(true);
      dragStartRef.current = {
        x: clientX,
        y: clientY,
        posX: localPosRef.current.x,
        posY: localPosRef.current.y,
      };
    },
    [isPublicView],
  );

  // Handle drag move
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent | TouchEvent) => {
      if (!dragStartRef.current) return;

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

      const deltaX = (clientX - dragStartRef.current.x) / scale;
      const deltaY = (clientY - dragStartRef.current.y) / scale;

      const newX = dragStartRef.current.posX + deltaX;
      const newY = dragStartRef.current.posY + deltaY;

      setLocalPos({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      if (dragStartRef.current) {
        // Save the final position using ref (not stale closure)
        onPositionChange(localPosRef.current.x, localPosRef.current.y);
        // Mark that we just finished dragging to prevent snap-back from stale props
        justFinishedDraggingRef.current = true;
      }
      setIsDragging(false);
      dragStartRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove);
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, scale, onPositionChange]);

  // Handle resize start
  const handleResizeStart = useCallback(
    (corner: string) => (e: React.MouseEvent | React.TouchEvent) => {
      if (isPublicView) return;

      e.stopPropagation();
      e.preventDefault();

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

      setIsResizing(true);
      resizeStartRef.current = {
        x: clientX,
        y: clientY,
        width: localSizeRef.current.width,
        height: localSizeRef.current.height,
        corner,
      };
    },
    [isPublicView],
  );

  // Handle resize move
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent | TouchEvent) => {
      if (!resizeStartRef.current) return;

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

      const deltaX = (clientX - resizeStartRef.current.x) / scale;
      const deltaY = (clientY - resizeStartRef.current.y) / scale;

      let newWidth = resizeStartRef.current.width;
      let newHeight = resizeStartRef.current.height;

      // Adjust based on which corner is being dragged
      const corner = resizeStartRef.current.corner;
      if (corner.includes('right')) {
        newWidth = Math.max(60, resizeStartRef.current.width + deltaX);
      }
      if (corner.includes('left')) {
        newWidth = Math.max(60, resizeStartRef.current.width - deltaX);
      }
      if (corner.includes('bottom')) {
        newHeight = Math.max(60, resizeStartRef.current.height + deltaY);
      }
      if (corner.includes('top')) {
        newHeight = Math.max(60, resizeStartRef.current.height - deltaY);
      }

      // Maintain aspect ratio by default
      const aspectRatio = resizeStartRef.current.width / resizeStartRef.current.height;
      if (corner === 'bottom-right' || corner === 'top-left') {
        const widthChange = Math.abs(newWidth - resizeStartRef.current.width);
        const heightChange = Math.abs(newHeight - resizeStartRef.current.height);
        if (widthChange > heightChange) {
          newHeight = newWidth / aspectRatio;
        } else {
          newWidth = newHeight * aspectRatio;
        }
      }

      setLocalSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      if (resizeStartRef.current) {
        // Save the final size using ref
        onSizeChange(localSizeRef.current.width, localSizeRef.current.height);
        // Mark that we just finished resizing to prevent snap-back from stale props
        justFinishedResizingRef.current = true;
      }
      setIsResizing(false);
      resizeStartRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove);
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isResizing, scale, onSizeChange]);

  // Use live icon position if provided, cached position if drag just ended, otherwise fall back to product position
  // This prevents the line from snapping back to the old position when icon drag ends (before DB update completes)
  const effectiveIconPosition =
    iconPosition ?? (justFinishedIconDragRef.current ? lastIconPositionRef.current : null);
  const productCenterX = effectiveIconPosition?.x ?? product.x_position ?? 0;
  const productCenterY = effectiveIconPosition?.y ?? product.y_position ?? 0;
  const imageCenterX = localPos.x + localSize.width / 2;
  const imageCenterY = localPos.y + localSize.height / 2;

  // Calculate the angle from icon center to image center
  const dx = imageCenterX - productCenterX;
  const dy = imageCenterY - productCenterY;
  const angle = Math.atan2(dy, dx);

  // Calculate LINE START point on the icon border (circular icon)
  // The icon is a circle with radius = iconSize / 2, scaled by product.scale
  const productScale = product.scale || 1.0;
  const iconRadius = (iconSize / 2) * productScale;

  // Line starts at the edge of the icon, in the direction of the image
  const lineStartX = productCenterX + iconRadius * Math.cos(angle);
  const lineStartY = productCenterY + iconRadius * Math.sin(angle);

  // Calculate LINE END point on the image border
  const halfWidth = localSize.width / 2;
  const halfHeight = localSize.height / 2;

  let lineEndX: number;
  let lineEndY: number;

  // Determine which edge of the image the line intersects
  // Use the angle from image center to icon center (opposite direction)
  const reverseAngle = angle + Math.PI;
  const tanReverseAngle = Math.abs(Math.tan(reverseAngle));
  const aspectRatioCalc = halfWidth / halfHeight;

  if (tanReverseAngle < aspectRatioCalc) {
    // Intersects left or right edge
    const xSign = Math.cos(reverseAngle) > 0 ? 1 : -1;
    lineEndX = imageCenterX + xSign * halfWidth;
    lineEndY = imageCenterY + xSign * halfWidth * Math.tan(reverseAngle);
  } else {
    // Intersects top or bottom edge
    const ySign = Math.sin(reverseAngle) > 0 ? 1 : -1;
    lineEndX = imageCenterX + (ySign * halfHeight) / Math.tan(reverseAngle);
    lineEndY = imageCenterY + ySign * halfHeight;
  }

  // Ensure all line coordinates are valid numbers
  const safeLineStartX = isNaN(lineStartX) ? productCenterX : lineStartX;
  const safeLineStartY = isNaN(lineStartY) ? productCenterY : lineStartY;
  const safeLineEndX = isNaN(lineEndX) ? imageCenterX : lineEndX;
  const safeLineEndY = isNaN(lineEndY) ? imageCenterY : lineEndY;

  return (
    <>
      {/* Connecting line - rendered first so it's behind the image */}
      <svg
        className="absolute inset-0 pointer-events-none"
        style={{ zIndex: 5, overflow: 'visible' }}
        data-product-callout-line={product.id}
      >
        <line
          x1={safeLineStartX}
          y1={safeLineStartY}
          x2={safeLineEndX}
          y2={safeLineEndY}
          stroke="#9CA3AF"
          strokeWidth={1.5 / scale}
          strokeDasharray={`${4 / scale} ${2 / scale}`}
        />
        {/* Small circle at the line start (near icon border) */}
        <circle cx={safeLineStartX} cy={safeLineStartY} r={3 / scale} fill="#9CA3AF" />
      </svg>

      {/* Image container - entire container is draggable */}
      <div
        className={cn(
          'absolute rounded-lg shadow-md overflow-hidden select-none group border border-gray-200',
          isDragging && 'cursor-grabbing shadow-lg',
          !isDragging && !isPublicView && 'cursor-grab',
          isSelected ? 'ring-2 ring-primary ring-offset-1' : '',
        )}
        data-product-callout-image={product.id}
        style={{
          left: localPos.x,
          top: localPos.y,
          width: localSize.width,
          height: localSize.height,
          zIndex: 15,
          pointerEvents: 'auto',
        }}
        onMouseDown={!isPublicView ? handleDragStart : undefined}
        onTouchStart={!isPublicView ? handleDragStart : undefined}
      >
        {/* Product image filling container */}
        <img
          src={imageUrl}
          alt={product.name}
          className="w-full h-full object-cover"
          draggable={false}
        />

        {/* Close button - appears on hover in top-right corner */}
        {!isPublicView && (
          <button
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              onClose();
            }}
            onMouseDown={e => e.stopPropagation()}
            className="absolute top-1 right-1 p-1 bg-black/60 hover:bg-black/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            title="Hide image"
          >
            <X className="w-3 h-3 text-white" />
          </button>
        )}

        {/* Resize handles - only show when not in public view */}
        {!isPublicView && (
          <>
            {/* Bottom-right corner resize handle */}
            <div
              className="absolute bottom-0 right-0 w-5 h-5 cursor-se-resize opacity-0 group-hover:opacity-100 transition-opacity"
              onMouseDown={e => {
                e.stopPropagation();
                handleResizeStart('bottom-right')(e);
              }}
              onTouchStart={e => {
                e.stopPropagation();
                handleResizeStart('bottom-right')(e);
              }}
            >
              <div className="absolute bottom-1 right-1 w-2 h-2 bg-primary rounded-sm" />
            </div>

            {/* Bottom-left corner resize handle */}
            <div
              className="absolute bottom-0 left-0 w-5 h-5 cursor-sw-resize opacity-0 group-hover:opacity-100 transition-opacity"
              onMouseDown={e => {
                e.stopPropagation();
                handleResizeStart('bottom-left')(e);
              }}
              onTouchStart={e => {
                e.stopPropagation();
                handleResizeStart('bottom-left')(e);
              }}
            >
              <div className="absolute bottom-1 left-1 w-2 h-2 bg-primary rounded-sm" />
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default ProductImageCallout;
